/**
 * Jovii — DOWNLOADS: the index and its state machine, with no browser in it.
 *
 * Every rule about what a download IS lives here as a pure function over plain
 * objects, so it can be argued with in a test rather than in a phone. The parts
 * that genuinely need a browser — OPFS handles, `fetch`, `navigator.storage` —
 * are in `store.ts` and `manager.svelte.ts` and call into this.
 *
 * ── THE ONE RULE THAT MATTERS: THE FILE IS THE TRUTH ───────────────────────────
 *
 * `DownloadEntry.bytes` is a CACHE of the file's length, never the authority. A
 * tab that dies mid-chunk leaves the index saying 400 MB and the file holding
 * 412 MB, or — worse, if the write landed and the index write did not — the index
 * saying 400 and the file holding 400 exactly. Trusting the index would re-fetch
 * bytes that are already here, or skip bytes that are not.
 *
 * So a resume ALWAYS asks the file how long it is (`resumeOffset` below is given
 * that number) and the index is reconciled to it. This is also why chunks are
 * written strictly sequentially and appended: an out-of-order write would make
 * "the file's length" mean nothing, and the resume offset is the whole reason
 * this design survives a reload at all.
 */

import type { ContentRef } from '$types';
// The app already has ONE size formatter and downloads must not become a second
// opinion about what "4.5 GB" means — release names are decimal GB and so is this.
import { fileSize } from '$lib/ui/format';
import type { DownloadEntry, DownloadState } from './types';

/** Bytes per range read while downloading. */
export const CHUNK_BYTES = 4 * 1024 * 1024;

/**
 * Head-room left free when deciding whether a film fits. A storage estimate is
 * an estimate — the browser rounds it, other origins move under it, and a quota
 * that is exactly big enough is a download that dies at 99%.
 */
export const QUOTA_HEADROOM_BYTES = 256 * 1024 * 1024;

/**
 * The same key shape `prefs.progressKey()` produces. Duplicated rather than
 * imported because prefs.svelte.ts is a RUNE module: importing it here would
 * drag `$state` into the pure layer and into every test of it. The two are
 * pinned together by `index-state.test.ts`, which asserts the shapes match.
 */
export function downloadKey(ref: Pick<ContentRef, 'kind' | 'imdbId' | 'season' | 'episode'>): string {
  return ref.kind === 'series'
    ? `series:${ref.imdbId}:${ref.season ?? 1}:${ref.episode ?? 1}`
    : `movie:${ref.imdbId}`;
}

/** The OPFS filename for a key. Colons are legal in OPFS names; dots are not special. */
export function fileNameFor(key: string): string {
  return `${key.replace(/[^A-Za-z0-9:._-]/g, '_')}.bin`;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Decoding what was persisted
 * ──────────────────────────────────────────────────────────────────────────── */

function isRef(value: unknown): value is ContentRef {
  if (typeof value !== 'object' || value === null) return false;
  const r = value as Record<string, unknown>;
  if (r.kind !== 'movie' && r.kind !== 'series') return false;
  if (typeof r.imdbId !== 'string' || !/^tt\d{5,10}$/.test(r.imdbId)) return false;
  if (typeof r.title !== 'string') return false;
  for (const k of ['season', 'episode'] as const) {
    if (r[k] !== undefined && !(Number.isSafeInteger(r[k]) && (r[k] as number) >= 0)) return false;
  }
  return true;
}

function bytes(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

const STATES: DownloadState[] = ['queued', 'downloading', 'done', 'failed'];

/**
 * One stored row back into an entry, or null.
 *
 * `downloading` DOES NOT SURVIVE A RELOAD. Nothing is running when a document
 * boots, so a row that still says "downloading" is a tab that died mid-file, and
 * leaving it in that state would draw a spinner that can never resolve. It comes
 * back as `queued`, which is exactly what it is: bytes on disk, nobody working
 * on it, press to carry on.
 */
export function decodeEntry(value: unknown): DownloadEntry | null {
  if (typeof value !== 'object' || value === null) return null;
  const r = value as Record<string, unknown>;
  if (typeof r.key !== 'string' || r.key.length === 0 || r.key.length > 96) return null;
  if (!isRef(r.ref)) return null;
  if (typeof r.title !== 'string') return null;
  if (!bytes(r.bytes) || !bytes(r.size)) return null;
  if (!bytes(r.addedAt)) return null;
  if (typeof r.state !== 'string' || !STATES.includes(r.state as DownloadState)) return null;

  const state = r.state === 'downloading' ? 'queued' : (r.state as DownloadState);
  const entry: DownloadEntry = {
    key: r.key,
    ref: r.ref,
    title: r.title,
    bytes: r.bytes,
    size: r.size,
    state,
    addedAt: r.addedAt
  };
  /* The running time survives a reload so a resumed conversion still has a
     total to count towards. `convertedSec` deliberately does NOT: nothing is
     running when a document boots, a remux restarts from zero, and a row that
     came back claiming nine minutes of converted film would be describing
     bytes that were thrown away. */
  if (typeof r.durationSec === 'number' && Number.isFinite(r.durationSec) && r.durationSec >= 0) {
    entry.durationSec = r.durationSec;
  }
  if (typeof r.episodeLabel === 'string') entry.episodeLabel = r.episodeLabel;
  if (typeof r.poster === 'string') entry.poster = r.poster;
  if (typeof r.candidateId === 'string') entry.candidateId = r.candidateId;
  if (typeof r.error === 'string' && state === 'failed') entry.error = r.error;
  return entry;
}

/** The whole index, dropping anything that no longer decodes. Newest first. */
export function decodeIndex(value: unknown): DownloadEntry[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: DownloadEntry[] = [];
  for (const raw of value) {
    const entry = decodeEntry(raw);
    // One row per key. A duplicate would be two rows pointing at one file, and
    // deleting either would empty the other.
    if (!entry || seen.has(entry.key)) continue;
    seen.add(entry.key);
    out.push(entry);
  }
  return out.sort((a, b) => b.addedAt - a.addedAt);
}

/* ────────────────────────────────────────────────────────────────────────────
 * Resuming
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Where the next byte read should start, given what the FILE says it holds.
 *
 * `fileBytes` is `(await handle.getFile()).size` — the only number that is not a
 * guess. Everything this returns is derived from it:
 *
 *   - past the known total (a source that changed size under us, a corrupt or
 *     half-truncated file) is not resumable, so start again from zero rather
 *     than append onto bytes that do not belong to this file;
 *   - exactly the total is finished, and re-reading it would be a whole film of
 *     wasted bandwidth to learn nothing;
 *   - anything else is the offset, whatever the index believed.
 *
 * A `size` of 0 means nothing has probed yet, so there is no total to compare
 * against and the file's own length is still the right place to carry on from.
 */
export function resumeOffset(fileBytes: number, size: number): number {
  if (!Number.isSafeInteger(fileBytes) || fileBytes < 0) return 0;
  if (size <= 0) return fileBytes;
  if (fileBytes > size) return 0;
  return fileBytes;
}

/** True when the file on disk is the whole thing. */
export function isComplete(entry: Pick<DownloadEntry, 'bytes' | 'size' | 'state'>): boolean {
  return entry.state === 'done' && entry.size > 0 && entry.bytes >= entry.size;
}

/**
 * Reconcile one entry against the file that is actually there.
 *
 * Called on boot for every row, and after every resume. A row whose file has
 * vanished — Safari evicted the origin, someone cleared site data — goes back to
 * `queued` with zero bytes rather than `done` with a file that is not there,
 * which is the difference between "press to download again" and a player that
 * opens a 0-byte file and reports the film as broken.
 *
 * IT ALSO OWNS THE `downloading` DEMOTION, and that is not a duplicate of
 * `decodeEntry`'s: reconcile is the function that runs when NOTHING IS RUNNING,
 * and a row that still says "downloading" at that moment is a tab that died,
 * whether it arrived through the decoder or was handed here directly. Leaving
 * the rule only in the decoder put the invariant in two places and let a
 * caller that already had entries in hand keep a spinner that can never
 * resolve — which is what the resume test caught.
 */
export function reconcile(entry: DownloadEntry, fileBytes: number | null): DownloadEntry {
  const resting: DownloadState = entry.state === 'downloading' ? 'queued' : entry.state;
  if (fileBytes === null) {
    return entry.bytes === 0 && resting === 'queued'
      ? entry
      : { ...entry, bytes: 0, state: 'queued' };
  }
  const at = resumeOffset(fileBytes, entry.size);
  const complete = entry.size > 0 && at >= entry.size;
  const state: DownloadState = complete ? 'done' : resting === 'done' ? 'queued' : resting;
  if (at === entry.bytes && state === entry.state) return entry;
  const next: DownloadEntry = { ...entry, bytes: at, state };
  if (state !== 'failed') delete next.error;
  return next;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The queue
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The next thing to work on: ONE AT A TIME, oldest first.
 *
 * Serial and not parallel, and that is the design rather than a simplification.
 * "Download Season 4" is ten episodes; ten concurrent whole-file reads through
 * one Real-Debrid account share the same ~690 KB/s per connection the media
 * engine already measured (see `range-reader.ts`), so ten at once finishes the
 * first episode last. Serial means episode one is watchable while episode two is
 * still arriving, which is the only ordering a person on a train can use.
 *
 * Oldest first (`addedAt` ascending) so a season downloads in the order it was
 * enqueued, which is episode order.
 */
export function nextInQueue(items: readonly DownloadEntry[]): DownloadEntry | null {
  const queued = items.filter((e) => e.state === 'queued');
  if (queued.length === 0) return null;
  return queued.reduce((oldest, e) => (e.addedAt < oldest.addedAt ? e : oldest));
}

/** Put an entry in, or replace the row that has its key. Newest first. */
export function upsert(items: readonly DownloadEntry[], entry: DownloadEntry): DownloadEntry[] {
  const rest = items.filter((e) => e.key !== entry.key);
  return [entry, ...rest].sort((a, b) => b.addedAt - a.addedAt);
}

export function withoutKey(items: readonly DownloadEntry[], key: string): DownloadEntry[] {
  return items.filter((e) => e.key !== key);
}

/* ────────────────────────────────────────────────────────────────────────────
 * Quota
 * ──────────────────────────────────────────────────────────────────────────── */

export interface QuotaVerdict {
  fits: boolean;
  /** Bytes free after the headroom. Negative is possible and means "already over". */
  freeBytes: number;
  /** The sentence the UI shows when it does not fit. Empty when it does. */
  message: string;
}

/**
 * Will this file fit? Asked BEFORE the first byte, because the alternative is
 * discovering it at 94% with a QuotaExceededError and a part-file to explain.
 *
 * An estimate of nothing (`quota` 0, or a browser that does not implement
 * `estimate`) is treated as "go ahead": refusing on a missing number would make
 * every download impossible on a browser that simply declines to answer, which
 * is worse than failing late on the rare device that is genuinely full.
 */
export function quotaVerdict(
  estimate: { quota?: number; usage?: number } | null,
  needBytes: number
): QuotaVerdict {
  const quota = estimate?.quota ?? 0;
  const usage = estimate?.usage ?? 0;
  if (quota <= 0) return { fits: true, freeBytes: Number.POSITIVE_INFINITY, message: '' };
  const free = quota - usage - QUOTA_HEADROOM_BYTES;
  if (needBytes <= 0 || needBytes <= free) return { fits: true, freeBytes: free, message: '' };
  return {
    fits: false,
    freeBytes: free,
    message:
      free <= 0
        ? `There is no room left on this device. Delete a download and try again.`
        : `That is ${fileSize(needBytes)} and only ${fileSize(free)} is free on this device. Delete a download and try again.`
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * What the screens read
 * ──────────────────────────────────────────────────────────────────────────── */

/** Bytes held on this device across every row, complete or not. */
export function totalBytes(items: readonly DownloadEntry[]): number {
  return items.reduce((sum, e) => sum + e.bytes, 0);
}

/** How far along, 0–100. A row with no known size reads 0 rather than NaN. */
export function percentOf(entry: Pick<DownloadEntry, 'bytes' | 'size'>): number {
  if (entry.size <= 0) return 0;
  return Math.max(0, Math.min(100, (entry.bytes / entry.size) * 100));
}

/** A size for a screen. `fileSize` answers '' for nothing; a list wants a zero. */
export function sizeLabel(n: number): string {
  return fileSize(n) || '0 MB';
}

/* ── GROUPED BY WHAT IT IS, NOT BY WHEN IT WAS ASKED FOR ────────────────────
 *
 * The owner, looking at eight rows of Silo: "we don't need to see two different
 * tiles for the downloads for the same series. It should be one that opens up
 * the list of downloaded episodes."
 *
 * So a series is ONE row that opens, and a film is one row that does not. This
 * is the same rule `history-groups.ts` applies to Continue watching, and for the
 * same reason: the thing on the shelf is the SHOW, and the episode is a detail
 * of where you are in it.
 *
 * A group carries its episodes in season/episode order (not the order they were
 * enqueued, which is the order a season download happens to run) and totals the
 * bytes it is actually holding, so the header's "used" number and the row's own
 * number are computed from the same field and cannot disagree.
 */

export interface DownloadGroup {
  /** `series:tt0903747` or the film's own entry key. Stable across a re-render. */
  key: string;
  kind: 'series' | 'movie';
  title: string;
  poster?: string;
  /** Season/episode order for a series; a single entry for a film. Never empty:
      a group only exists because an entry created it, which is why the screen
      can read `entries[0]` without a guard. The tuple type says so. */
  entries: [DownloadEntry, ...DownloadEntry[]];
  /** How many are finished and playable right now. */
  ready: number;
  /** Bytes on disk for the whole group. */
  bytes: number;
  /** The one being fetched right now, if any — the row's ring follows it. */
  active: DownloadEntry | null;
}

export function groupDownloads(items: readonly DownloadEntry[]): DownloadGroup[] {
  const groups = new Map<string, DownloadGroup>();
  const order: string[] = [];
  for (const entry of items) {
    const series = entry.ref.kind === 'series';
    const key = series ? `series:${entry.ref.imdbId}` : entry.key;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        kind: series ? 'series' : 'movie',
        title: entry.title,
        ...(entry.poster !== undefined ? { poster: entry.poster } : {}),
        entries: [entry],
        ready: 0,
        bytes: 0,
        active: null
      };
      groups.set(key, group);
      order.push(key);
    } else {
      group.entries.push(entry);
    }
    group.bytes += entry.bytes;
    if (entry.state === 'done') group.ready += 1;
    if (entry.state === 'downloading') group.active = entry;
  }
  for (const group of groups.values()) {
    group.entries.sort(
      (a, b) =>
        (a.ref.season ?? 0) - (b.ref.season ?? 0) ||
        (a.ref.episode ?? 0) - (b.ref.episode ?? 0) ||
        a.addedAt - b.addedAt
    );
  }
  return order.map((key) => groups.get(key)!);
}

/** What a group's row says under its name. One line, and it answers "can I watch it". */
export function groupStatus(group: DownloadGroup): string {
  if (group.kind === 'movie') return '';
  const total = group.entries.length;
  if (group.active) {
    const label = group.active.episodeLabel ?? 'an episode';
    // "Converting" once the remuxer is writing: the episode is being rewritten
    // to MP4 on this device, which is a different wait from a download and the
    // longer half of it. Same rule as Downloads.svelte's statusLine.
    const verb = group.active.convertedSec !== undefined ? 'Converting' : 'Downloading';
    return `${verb} ${label} · ${group.ready} of ${total} ready`;
  }
  const waiting = group.entries.filter((e) => e.state === 'queued').length;
  if (waiting > 0) return `${group.ready} of ${total} ready · ${waiting} waiting`;
  const failed = group.entries.filter((e) => e.state === 'failed').length;
  if (failed > 0) return `${group.ready} of ${total} ready · ${failed} stopped`;
  return `${total} ${total === 1 ? 'episode' : 'episodes'} · ${sizeLabel(group.bytes)}`;
}